import { Button, CIInfoIcon, CILabel, Input, Modal, accessToken, Textarea } from 'ccs-ui-components';
import { useEffect, useRef, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import Form from 'react-bootstrap/Form';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { HELP_CENTER_CHATPAGE_SERVICE_BASE_URL, SCHEMA_NAME_MAXLENGTH, STR } from '../../../../constants';
import { formParam } from '../../../../hooks/events/EventFormValidation';
import { ISchemaDetailsForm } from '../../../../models/index';
import { useSelector } from '../../../../store/AppStore';
import SchemaGeneratorWrapper from '../../../schema-generator/SchemaGeneratorWrapper';
import classes from './SchemaDetailsForm.module.scss';

const SchemaDetailsForm = ({ formData, handleInputChange, setFormParamValues, preview, isCheckClicked, isEditFormClicked, setSensitiveFieldsArr, sensitiveFieldsArr }: ISchemaDetailsForm) => {
  const { t: translate } = useTranslation();
  const location = useLocation();
  const [showAvroModal, setShowAvroModal] = useState(false);
  const [isConfirmClick, setIsConfirmClick] = useState(false);
  const [isAvroConfirmDisabled, setIsAvroConfirmDisabled] = useState(true);
  const [jsonInput, setJsonInput] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [instructions, setInstructions] = useState('');
  const [uploadedFileName, setUploadedFileName] = useState('');
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [showAiAssistModal, setShowAiAssistModal] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const eventListItemIndex = location.state as { index: number | null, creatreNewEventButtonClicked: boolean | null };
  const { eventDetails, isLoading } = useSelector(
    (state) => state.getEventIdReducer
  );

  const avroValidation = !formData.avroSchema.isValid && formData.avroSchema.isTouched;
  const isSensitiveFieldExist = !!formData.avroSchema.value && isCheckClicked && !sensitiveFieldsArr.length;
  const [isGeneratingSchemaName, setIsGeneratingSchemaName] = useState(false);

  /**
   * @method disableAvroSchemaConfirm
   * @description Disables or enables the Avro schema confirmation.
   * @param disable - A boolean value indicating whether to disable or enable the Avro schema confirmation.
   */
  const disableAvroSchemaConfirm = (disable: boolean) => {
    setIsAvroConfirmDisabled(disable);
  };

  const HELPER_TEXT = {
    schemaName_ht: translate('helper_text.txt_schemaName_ht'),
    avro_ht: translate('helper_text.txt_avro_ht'),
  };

  /**
   * This block will execute on first time when pages becomes visible and identify the navigation type
   * if user navigates from the event list page, set the event list item values
   * else set the default empty values
   */
  useEffect(() => {
    // if user navigates form event list page, set the event list item values
    if (!isLoading && eventDetails && (preview || isEditFormClicked)) {
      const [eventItem] = eventDetails;
      const newFormData = { ...formData };
      if (eventItem?.eventSchemaId && eventItem?.eventSchemaVersion) {
        // fetch the avro schema
        newFormData.avroSchema.value = eventItem?.schema;
        newFormData.avroSchema.isValid = true;
        newFormData.schemaName.value = eventItem?.eventSchemaId;
        newFormData.schemaName.isValid = true;
        setFormParamValues(newFormData);
      } else if (eventListItemIndex?.creatreNewEventButtonClicked) {
        const newFormData = { ...formParam };
        newFormData.avroSchema.value = '';
        setFormParamValues(newFormData);
      }
    }
  }, [eventDetails, isLoading]);

  /**
   * @method formatJsonForInput
   * @description Gets the json formatted data
   * @returns json formatted data
   */
  const formatJsonForInput = () => {
    let jsonData = formData.avroSchema.value;
    try {
      jsonData = jsonData ? JSON.stringify(JSON.parse(jsonData), null, 4) : jsonData;
    } catch (e) {
      jsonData = formData.avroSchema.value;
    }
    return jsonData;
  };

  /**
   * @method handleAvroModalClick
   * @description Handles the click event for the Avro modal.
   * If the preview is false and the Avro schema is valid or not provided,
   * it sets the isConfirmClick state to false, shows the Avro modal,
   * and sets the sensitiveFieldsArr state.
   */
  const handleAvroModalClick = () => {
    if (!preview && ((formData.avroSchema.value && formData.avroSchema.isValid) || !formData.avroSchema.value)) {
      setIsConfirmClick(false);
      setShowAvroModal(true);
      setSensitiveFieldsArr(sensitiveFieldsArr);
    }
  };

  /**
   * @method handleAiAssistModalClick
   * @description Handles the click event for the AI Assist modal.
   * Opens the AI Assist modal if preview is false.
   */
  const handleAiAssistModalClick = () => {
    if (!preview) {
      setShowAiAssistModal(true);
    }
  };

  /**
   * @description Closes the Avro modal dialog.
   */
  const handleCloseAvroModalDialog = () => setShowAvroModal(false);
  const handleCloseAiAssistModalDialog = () => {
    setShowAiAssistModal(false);
    // Reset form state when modal closes
    setJsonInput('');
    setInstructions('');
    setUploadedFileName('');
    setUploadedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  /**
   * @method handleFileUpload
   * @description Handles file upload for .json and .txt files
   * @param event - File input change event
   */
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const fileName = file.name.toLowerCase();
    const isValidFile = fileName.endsWith('.json') || fileName.endsWith('.txt');

    if (!isValidFile) {
      alert('Please upload a .json or .txt file');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    // Validate file content is plain text (not RTF or other formats)
    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result as string;
      // Check if file contains RTF format markers
      if (content.includes('{\\rtf1') || content.includes('\\rtf1\\ansi')) {
        alert('Invalid file format. Please upload a plain text JSON file, not an RTF file. Make sure the file is saved as plain text (.json or .txt), not rich text format.');
        setUploadedFileName('');
        setUploadedFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }
      // File is valid, proceed
      setUploadedFileName(file.name);
      setUploadedFile(file);
      // Clear JSON input when file is uploaded
      setJsonInput('');
    };
    reader.onerror = () => {
      alert('Error reading file. Please try again.');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    };
    reader.readAsText(file);
  };

  /**
   * @method handleUploadButtonClick
   * @description Triggers the file input click
   */
  const handleUploadButtonClick = () => {
    fileInputRef.current?.click();
  };

  /**
   * @method handleRemoveFile
   * @description Removes the uploaded file and clears related state
   */
  const handleRemoveFile = () => {
    setUploadedFileName('');
    setUploadedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  /**
   * @method handleJsonInputChange
   * @description Handles JSON input textarea change and clears uploaded file if JSON is entered
   * @param event - Textarea change event
   */
  const handleJsonInputChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const { value } = event.target;
    setJsonInput(value);
    // Clear uploaded file when JSON input is entered
    if (value.trim()) {
      setUploadedFileName('');
      setUploadedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  /**
   * @method handleInstructionsChange
   * @description Handles instructions textarea change
   * @param event - Textarea change event
   */
  const handleInstructionsChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInstructions(event.target.value);
  };

  /**
   * @description Closes the AI Assist modal dialog.
   */
  const handleGenerateSchema = async () => {
    const hasFile = uploadedFile !== null;
    const hasJsonInput = jsonInput.trim().length > 0;

    if ((!hasFile && !hasJsonInput) || !formData.schemaName.value) return;

    setIsGenerating(true);
    try {
      // Hardcoded schema name for testing
      const actualSchemaName = 'UserRegistrationEvent';
      const baseUrl = HELP_CENTER_CHATPAGE_SERVICE_BASE_URL;

      let response;

      if (hasFile && uploadedFile) {
        // Use multipart/form-data for file upload
       // const fileApiUrl = `${baseUrl}/generate-avro-from-file?event_schema_name=${encodeURIComponent(actualSchemaName || '')}`;
        const fileApiUrl = `${baseUrl}/generate-avro-from-file`;
        const multipartFormData = new FormData();
        multipartFormData.append('file', uploadedFile);
        multipartFormData.append('event_schema_name', actualSchemaName || '');

        // Include instructions if provided
        if (instructions.trim()) {
          multipartFormData.append('instructions', instructions.trim());
        }

        response = await axios.post(fileApiUrl, multipartFormData);
      } else {
        // Use JSON body flow for JSON input - matches curl command format
        const contentApiUrl = `${baseUrl}/generate-avro-from-content`;
        const requestBody: { content: string; event_schema_name: string; instructions?: string } = {
          content: JSON.stringify(JSON.parse(jsonInput.trim())),
          event_schema_name: actualSchemaName || ''
        };

        // Include instructions if provided
        if (instructions.trim()) {
          requestBody.instructions = instructions.trim();
        }

        response = await axios.post(contentApiUrl, requestBody, {
          headers: {
            'Content-Type': 'application/json'
          }
        });
      }

      const avro = response.data.avro_schema;
      setFormParamValues((prev) => {
        const updated = structuredClone(prev);
        updated.avroSchema.value = JSON.stringify(avro, null, 2);
        updated.avroSchema.isValid = true;
        return updated;
      });
      handleCloseAiAssistModalDialog();
    } catch (err) {
      console.error('Failed to generate schema', err);
      alert('Failed to generate schema. Please check your input and try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  /**
   * @method onAvroConfirmClickHandler
   * @description Handles the click event when confirming the Avro schema.
   * @param value - The Avro schema value.
   * @param encryptFieldExist - Indicates whether an encrypt field exists.
   */
  const onAvroConfirmClickHandler = (value: string, encryptFieldExist: boolean) => {
    setIsConfirmClick(true);
    setShowAvroModal(encryptFieldExist);
    if (typeof value === STR) {
      setFormParamValues((preVal) => {
        const newFormData = structuredClone(preVal);
        newFormData.avroSchema.isTouched = true;
        newFormData.avroSchema.value = value;
        newFormData.avroSchema.isValid = true;
        newFormData.avroSchema.error = '';
        return newFormData;
      });
    }
  };

   const handleSuggestSchemaNameClick = async () => {
    if (!formData.eventName.value) {
      return;
    }
    setIsGeneratingSchemaName(true);
    try {
      const eventName = formData.eventName.value; // Get event name
      const headers = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken.value}`,
      };
      const apiUrl = `${HELP_CENTER_CHATPAGE_SERVICE_BASE_URL}/generate-schema-name?event_name=${eventName}`;
      const response = await axios.get(apiUrl, { headers });
      if (response.data?.schema_name && setFormParamValues) {
        setFormParamValues((previousState) => {
          const data = structuredClone(previousState);
          data.schemaName.value = response.data.schema_name.trim();
          data.schemaName.isValid = true;
          data.schemaName.isTouched = true;
          return data;
        });
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Error generating AI suggestion. Please try again.');
    } finally {
      setIsGeneratingSchemaName(false);
    }
  };

  /**
   * Renders the modal body for the SchemaDetailForms component.
   * @returns The JSX element representing the modal body.
   */
  const modalBody = (
    <div>
      <SchemaGeneratorWrapper
        isCheckClicked={isCheckClicked}
        onAvroConfirmClickHandler={onAvroConfirmClickHandler}
        isConfirmClick={isConfirmClick}
        formData={formData}
        setSensitiveFieldsArr={setSensitiveFieldsArr}
        sensitiveFieldsArr={sensitiveFieldsArr}
        disableAvroschemaConfirm={disableAvroSchemaConfirm}
      />
    </div>
  );

  const aiAssistModalBody = (
    <div className={classes['ai-assist-modal-body']}>
      <div className={classes['upload-section']}>
        <p className={classes['upload-description']}>
          Upload a JSON/TXT file or paste JSON input, along with optional instructions, to get AI assistance in generating your Avro schema.
        </p>
        <div style={{ marginBottom: '16px' }}>
          <input
            ref={fileInputRef}
            type="file"
            accept=".json,.txt"
            onChange={handleFileUpload}
            style={{ display: 'none' }}
            data-testid="file-upload-input"
          />
          <Button
            variant="secondary"
            onClick={handleUploadButtonClick}
            style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
            data-testid="upload-file-btn"
            disabled={!!jsonInput.trim()}
          >
            <i className="bi bi-upload" style={{ fontSize: '16px' }} />
            Upload File (.json, .txt)
          </Button>
          {uploadedFileName && (
            <div style={{ marginTop: '8px', fontSize: '14px', color: '#28a745', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <i className="bi bi-check-circle" style={{ marginRight: '4px' }} />
              <span>{uploadedFileName}</span>
              <button
                type="button"
                onClick={handleRemoveFile}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  color: '#dc3545'
                }}
                data-testid="remove-file-btn"
                aria-label="Remove file"
              >
                <i className="bi bi-x-circle" style={{ fontSize: '18px' }} />
              </button>
            </div>
          )}
        </div>
        <div style={{ marginBottom: '16px' }}>
          <div style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500 }}>
            JSON Input
          </div>
          <Textarea
            label=""
            name="jsonInput"
            value={jsonInput}
            onChange={handleJsonInputChange}
            style={{ height: '300px' }}
            placeholder="Paste your JSON here"
            maxLength="1048576"
            disabled={!!uploadedFile}
          />
        </div>
        <div style={{ marginBottom: '16px' }}>
          <div style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500 }}>
            Instructions (Optional)
          </div>
          <Textarea
            label=""
            name="instructions"
            value={instructions}
            onChange={handleInstructionsChange}
            style={{ height: '300px' }}
            maxLength="1048576"
            placeholder="Paste your instructions here"
            data-testid="instructions-input"
            disabled={!jsonInput.trim() && !uploadedFile}
          />
        </div>
      </div>
    </div>
  );

  return (
    <>
      <Form className={classes['form-container']} onSubmit={(event) => { event.preventDefault(); }} data-testid="schemaDetailForm">
        <fieldset disabled={preview}>
          <Form.Group controlId="schema.ControlInput1" className="mb-3">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <label htmlFor="schemaName">{String(translate('schema_details_form_components.lbl_schema_name'))}</label>
              <div className={classes['jolt-config-beta-link']}>
                <div className={classes['button-group']}>
                  <Button
                    variant="tertiary"
                    className={classes['configure-jolt-btn']}
                    onClick={handleSuggestSchemaNameClick}
                    disabled={!formData.eventName.value || isGeneratingSchemaName}
                    data-testid="suggest-schema-name-btn"
                  >
                    <small>{isGeneratingSchemaName ? 'Generating...' : 'Suggest Schema Name'}</small>
                    <CIInfoIcon iconTxt={String(!formData.eventName.value ? 'Enter event name first' : 'Get AI suggestion for schema name')} />
                  </Button>
                  <span className={classes['btn-beta-text']}>[Beta]</span>
                </div>
              </div>
            </div>
            <Input
              isMandatory
              // labelText={String(translate('schema_details_form_components.lbl_schema_name'))}
              infoIconTxt={String(translate('schema_details_form_components.schema_name_info_bubble'))}
              error={!formData.schemaName.isValid}
              type="text"
              placeholder={String(translate('schema_details_form_components.plchd_schema_name'))}
              name="schemaName"
              label=""
              value={formData.schemaName.value}
              onChange={handleInputChange}
              maxLength={Number(SCHEMA_NAME_MAXLENGTH)}
              data-testid="schemaName"
              errorTxt={formData.schemaName.error}
              helperText={HELPER_TEXT.schemaName_ht}
            />
          </Form.Group>
          <Form.Group controlId="schema.ControlTextarea1">
            <Row>
              <Col sm={6}>
                <CILabel isMandatory id="avroSchemaDesc" labelText={String(translate('schema_details_form_components.lbl_schema_description'))} infoIconText={String(translate('schema_details_form_components.event_schema_info_bubble'))} helperText={HELPER_TEXT.avro_ht} />
              </Col>
              <Col sm={6}>
                <div className={classes['radio-divs']}>
                  <div className={classes['btn-manual-radio']}>
                    <Button
                      variant="tertairy"
                      onClick={handleAvroModalClick}
                      className={classes['btn-manual-text']}
                      disabled={((preview) || (!preview && (!!formData.avroSchema.value && !formData.avroSchema.isValid)))}
                      data-testid="build-avro-btn"
                    >
                      {translate('schema_details_form_components.build_avro_schema')}
                      <CIInfoIcon iconTxt={String(translate('schema_details_form_components.build_avro_schema_info_bubble'))} />
                    </Button>
                    <span className={classes['btn-beta-text']}>{translate('schema_details_form_components.beta_title')}</span>
                  </div>
                  <div className={classes['btn-manual-radio']} style={{ marginTop: '8px' }}>
                    <Button
                      variant="tertairy"
                      onClick={handleAiAssistModalClick}
                      className={classes['btn-manual-text']}
                      disabled={preview || !formData.eventName.value || !formData.schemaName.value}
                      data-testid="ai-assist-btn"
                    >
                      {translate('schema_details_form_components.ai_assist_button')}
                      <CIInfoIcon iconTxt={String(translate('schema_details_form_components.ai_assist_button_info_bubble'))} />
                    </Button>
                    <span className={classes['btn-beta-text']}>{translate('schema_details_form_components.beta_title')}</span>
                  </div>
                </div>
              </Col>
            </Row>
            <Textarea
              label=""
              name="avroSchema"
              id="avroSchemaDesc"
              placeholder={String(translate('schema_details_form_components.plchd_schema_description'))}
              value={formatJsonForInput()}
              onChange={handleInputChange}
              error={avroValidation || isSensitiveFieldExist}
              maxLength=""
              data-testid="avro-schema"
            />
          </Form.Group>
          <div className={classes['error-container']}>
            {avroValidation ? (
              <h6 className={classes.error}>{formData.avroSchema.error}</h6>
            ) : null}
            {isSensitiveFieldExist && (
              <h6 className={classes.error}>{translate('schema_details_form_components.specify_field_for_encryption')}</h6>
            )}
          </div>
        </fieldset>
      </Form>
      <Modal
        modalTitle={translate('schema_details_form_components.avro_schema_builder')}
        btnCloseText={String(translate('schema_details_form_components.avro_modal_cancel'))}
        btnSaveText={String(translate('schema_details_form_components.avro_modal_confirm'))}
        modalBody={modalBody}
        modalShow={showAvroModal}
        onCloseModal={handleCloseAvroModalDialog}
        onSaveModal={onAvroConfirmClickHandler}
        alignToCentre
        disableSaveBtn={isAvroConfirmDisabled}
        disableCloseBtn={false}
        className={classes['avroschema-modal']}
      />
      <Modal
        modalTitle={translate('schema_details_form_components.ai_assist_modal_title')}
        btnCloseText={String(translate('schema_details_form_components.ai_assist_modal_cancel'))}
        btnSaveText={String(translate('schema_details_form_components.ai_assist_modal_generate_button'))}
        modalBody={aiAssistModalBody}
        modalShow={showAiAssistModal}
        onCloseModal={handleCloseAiAssistModalDialog}
        onSaveModal={handleGenerateSchema}
        alignToCentre
        disableSaveBtn={(!uploadedFile && !jsonInput.trim()) || !formData.schemaName.value || isGenerating}
        disableCloseBtn={false}
        className={classes['ai-assist-modal']}
      />
    </>
  );
};

export default SchemaDetailsForm;
